import { css } from '@emotion/react';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import {
  Alert,
  Box,
  Button,
  Flex,
  Text,
  desktopFirstMediaQuery,
  openConfirmModal,
  useShowNotification,
} from '@inflearn/ds-react-next';
import { useLang } from '@inflearn/i18n-tools';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import type { CredentialResponse } from '../../../../../../../resources/@types/accountSchema';
import { useOpenEditCancelConfirmModal } from '../../../../../../hooks/useOpenEditCancelConfirmModal';
import { useClientAuthChangePhoneNumberCreateMutation } from '../../../../../../mutations/useClientAuthPhoneVerificationCheckCreateVariables';
import useClientAccountCredentialsQuery from '../../../../../../queries/useClientAccountCredentialsQuery';
import { PhoneNumberValidation } from '../../../../../../validators/PhoneNumberValidation';
import { PhoneVerificationForm } from '../../../../../common/PhoneVerificationForm';
import VerificationCodeInput, {
  type VerificationCodeInputProps,
} from '../../../../../common/VerificationCodeInput';
import { SettingRow } from '../../common/SettingRow';
import type { BasicSectionRowProps } from '../BasicSection';

export const PhoneSettingRow = ({
  showEditButton,
  onClose,
  onClick,
  isEditMode,
}: BasicSectionRowProps) => {
  const { t } = useTranslation();
  const { countryCode } = useLang();

  const [isSend, setIsSend] = useState(false);
  const { data, isSuccess } = useClientAccountCredentialsQuery({
    suspense: true,
    useErrorBoundary: false,
  });
  const queryClient = useQueryClient();
  const { showNotification } = useShowNotification();

  const { openEditCancelConfirmModal } = useOpenEditCancelConfirmModal();
  const form = useForm<PhoneNumberValidation>({
    mode: 'all',
    resolver: classValidatorResolver(PhoneNumberValidation),
    defaultValues: {
      countryCode,
    },
  });

  const openCompleteModal = (phone: string, isSignUpDiscountDeleted: boolean) => {
    openConfirmModal({
      title: t('인증 완료'),
      children: (
        <Flex direction="column" gap={16}>
          <Alert type="default" title={toPhoneFormat(phone)} />
          <Text size="md" c="gray.7">
            {t('위 번호로 휴대폰 번호 인증이 완료되었습니다.')}

            <br />
            {isSignUpDiscountDeleted &&
              t(
                '신규가입 할인을 한 번이라도 받으신 적이 있는 경우, 타 계정에서 중복 적용이 불가합니다.',
              )}
          </Text>
        </Flex>
      ),

      withCloseButton: true,
      closeOnClickOutside: true,
      onConfirm: () => {
        onClose?.();
        showNotification({
          title: t('저장이 완료되었습니다.'),
          type: 'success',
        });
      },
      labels: {
        confirm: t('확인'),
      },
      footerType: 'right',
      confirmProps: {
        color: 'infgreen',
        type: 'submit',
        size: 'md',
      },
    });
  };

  const phoneNumberChangeMutation = useClientAuthChangePhoneNumberCreateMutation({
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries(useClientAccountCredentialsQuery.queryKey);
      onClose?.();
      openCompleteModal(variables.phoneNumber, Boolean(res.data?.isSignUpDiscountDeleted));
    },
  });

  const { isDirty } = form.formState;

  useEffect(() => {
    if (!isEditMode) {
      form.clearErrors();
      form.reset();
      setIsSend(false);

      return;
    }

    updateForm(data);
  }, [isEditMode, data]);

  useEffect(() => {
    if (!data || !isSuccess) {
      return;
    }

    updateForm(data);
  }, [data, isSuccess]);

  const updateForm = (formData?: CredentialResponse | null) => {
    if (!formData) {
      return;
    }

    form.setValue('phone', formData.phoneNumber);
    form.setValue('countryCode', formData.countryCode || countryCode);
  };

  const handleClose = () => {
    if (data) {
      form.reset(data);
    }

    if (!isDirty) {
      onClose?.();

      return;
    }

    openEditCancelConfirmModal({
      onConfirm: () => {
        onClose?.();
      },
    });
  };

  const onClickCompletes: VerificationCodeInputProps['onClickCompletes'] = (formData, options) => {
    phoneNumberChangeMutation.mutate(
      {
        phoneNumber: formData.phone,
        countryCode: formData.countryCode,
        authCode: formData.verifyCode,
      },
      options,
    );
  };

  return (
    <FormProvider {...form}>
      <SettingRow label={t('휴대폰 번호')} align={isEditMode ? 'flex-start' : 'center'}>
        {isEditMode ? (
          <Flex w="100%" direction="column" gap={8}>
            <Box>
              <PhoneVerificationForm
                autoFocus
                showPhoneInputLabel={false}
                onSend={() => setIsSend(true)}
                isSend={isSend}
                existingPhoneNumber={data?.phoneNumber}
                styleSubmitButton={css({ [desktopFirstMediaQuery.mobile]: { width: '100%' } })}
              />
              {isSend && (
                <VerificationCodeInput
                  onClickCompletes={onClickCompletes}
                  styleForm={css({
                    marginTop: 16,

                    [desktopFirstMediaQuery.mobile]: {
                      flexWrap: 'wrap',
                      button: { width: '100%' },
                    },
                  })}
                />
              )}
            </Box>
            <Button
              color="gray"
              variant="subtle"
              size="sm"
              radius="md"
              onClick={handleClose}
              ml="auto"
            >
              {t('취소')}
            </Button>
          </Flex>
        ) : (
          <Flex w="100%" justify="space-between" align="center">
            <Text
              fz={16}
              c={`gray.${data?.phoneNumber ? '9' : '5'}`}
              fw={data?.phoneNumber ? 600 : undefined}
            >
              {data?.phoneNumber
                ? toPhoneFormat(data?.phoneNumber)
                : t('휴대폰 번호를 인증해 주세요.')}
            </Text>

            {showEditButton && (
              <Button variant="default" size="sm" radius="md" onClick={onClick}>
                {t('설정')}
              </Button>
            )}
          </Flex>
        )}
      </SettingRow>
    </FormProvider>
  );
};

/**
 * 휴대폰 번호를 입력받아서 010-1234-5678 형식으로 변환합니다.
 */
function toPhoneFormat(phone: string) {
  return phone.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3');
}
